import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Plus,
  Edit,
  Mail,
  Phone,
  DollarSign,
  Clock,
  Loader2,
  Users,
  MapPin,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCompany } from '@/hooks/use-company';
import { useRBAC } from '@/hooks/use-rbac';
import { CompanyFormModal } from '@/components/settings';
import { CompanyCreateAccessDialog } from '@/components/settings/company-create-access-dialog';
import { useCanCreateCompany } from '@/hooks/use-can-create-company';
import { BranchFormModal } from '@/components/settings/branch-form-modal';
import { DeleteCompanyModal } from '@/components/settings/delete-company-modal';
import { InactivateBranchModal } from '@/components/settings/inactivate-branch-modal';
import { getCompanyBranch, type DetailedCompany, type CompanyBranch } from '@/services/company';
import { queryKeys } from '@/lib/query/keys';

export const Route = createFileRoute('/(app)/settings/')({
  component: SettingsPage,
});

function SettingsPage() {
  const { companies, isLoading: isLoadingCompanies, selectedCompany } = useCompany();
  const { roleLevel, canAccessCompany, canAccessAllBranches } = useRBAC();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const { canCreate, isLoading: isSubscriptionChecking } = useCanCreateCompany();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<DetailedCompany | null>(null);
  const [isAddBranchDialogOpen, setIsAddBranchDialogOpen] = useState(false);
  const [isEditBranchDialogOpen, setIsEditBranchDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<CompanyBranch | null>(null);
  const [branchCompanyId, setBranchCompanyId] = useState<number | null>(null);
  const [isDeleteCompanyDialogOpen, setIsDeleteCompanyDialogOpen] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState<DetailedCompany | null>(null);
  const [isInactivateBranchDialogOpen, setIsInactivateBranchDialogOpen] = useState(false);
  const [inactivatingBranch, setInactivatingBranch] = useState<CompanyBranch | null>(null);

  // Gate creation on an active subscription (like the phone apps): block up
  // front rather than letting a company shell get created. While the check is
  // still loading we don't act -- the button is disabled -- so a click during
  // that window can't slip past the gate on the fail-open default.
  const handleAddCompany = () => {
    if (isSubscriptionChecking) return;
    if (canCreate) {
      setIsAddDialogOpen(true);
    } else {
      setShowAccessDialog(true);
    }
  };

  const handleEditCompany = (company: DetailedCompany) => {
    setEditingCompany(company);
    setIsEditDialogOpen(true);
  };

  const handleAddBranch = (companyId: number) => {
    setBranchCompanyId(companyId);
    setIsAddBranchDialogOpen(true);
  };

  const handleEditBranch = (branch: CompanyBranch) => {
    // Find which company this branch belongs to
    const company = companies.find((c) => 
      c.company_branch?.some((b) => b.id === branch.id) || 
      c.id === branch.company_id
    );
    if (company) {
      setBranchCompanyId(company.id);
    }
    setEditingBranch(branch);
    setIsEditBranchDialogOpen(true);
  };


  const handleDeleteCompany = (company: DetailedCompany) => {
    setDeletingCompany(company);
    setIsDeleteCompanyDialogOpen(true);
  };

  const handleInactivateBranch = (branch: CompanyBranch) => {
    // Show inactivate modal
    setInactivatingBranch(branch);
    setIsInactivateBranchDialogOpen(true);
  };

  const getCompanyInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format time from HH:mm format to 12-hour format
  const formatTime = (time: string) => {
    if (!time) return '';
    
    // If time already contains AM/PM (from API), return as-is
    if (time.includes('AM') || time.includes('PM') || time.includes('am') || time.includes('pm')) {
      return time.trim();
    }
    
    // Otherwise, convert from 24-hour format to 12-hour format
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format working hours range
  const formatWorkingHours = (branch: CompanyBranch) => {
    if (branch.start_time && branch.end_time) {
      return `${formatTime(branch.start_time)} - ${formatTime(branch.end_time)}`;
    }
    return '—';
  };

  // Show loading state while company data is being fetched
  if (isLoadingCompanies) {
    return (
      <div className="container max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Page-level access control - L3 cannot access settings page
  if (!canAccessCompany) {
    return (
      <div className="container max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Access Denied</h3>
            <p className="text-muted-foreground mt-1 text-center">
              You don't have permission to access the settings page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-2">Manage your companies</p>
      </div>

      {/* Your Companies Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-lg">Your Companies</CardTitle>
                <CardDescription>
                  {isLoadingCompanies
                    ? 'Loading...'
                    : `${companies.length} ${companies.length === 1 ? 'company' : 'companies'} registered`}
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleAddCompany} disabled={isSubscriptionChecking} className="gap-2 w-full sm:w-auto" size="sm">
              <Plus className="h-4 w-4" />
              Add Company
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Company Details Section */}
      {isLoadingCompanies ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : companies.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No companies yet</h3>
            <p className="text-muted-foreground mt-1 mb-4">
              Add your first company to get started
            </p>
            <Button onClick={handleAddCompany} disabled={isSubscriptionChecking} variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              Add Company
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {companies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              onEdit={() => handleEditCompany(company)}
              onAddBranch={() => handleAddBranch(company.id)}
              onEditBranch={handleEditBranch}
              onDeleteCompany={handleDeleteCompany}
              onInactivateBranch={handleInactivateBranch}
              formatWorkingHours={formatWorkingHours}
              getCompanyInitials={getCompanyInitials}
              roleLevel={roleLevel}
              canAccessAllBranches={canAccessAllBranches}
              userBranchId={selectedCompany?.company_branch_id ?? null}
            />
          ))}
        </div>
      )}

      {/* Add Company Dialog */}
      <CompanyFormModal
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        mode="add"
      />

      <CompanyCreateAccessDialog
        open={showAccessDialog}
        onOpenChange={setShowAccessDialog}
      />

      {/* Edit Company Dialog */}
      <CompanyFormModal
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditingCompany(null);
        }}
        mode="edit"
        company={editingCompany || undefined}
      />

      {/* Add Branch Dialog */}
      {branchCompanyId && (
        <BranchFormModal
          open={isAddBranchDialogOpen}
          onClose={() => {
            setIsAddBranchDialogOpen(false);
            setBranchCompanyId(null);
          }}
          mode="add"
          companyId={branchCompanyId}
        />
      )}

      {/* Edit Branch Dialog */}
      {editingBranch && branchCompanyId && (
        <BranchFormModal
          open={isEditBranchDialogOpen}
          onClose={() => {
            setIsEditBranchDialogOpen(false);
            setEditingBranch(null);
            setBranchCompanyId(null);
          }}
          mode="edit"
          companyId={branchCompanyId}
          branch={editingBranch}
        />
      )}

      {/* Delete Company Dialog */}
      {deletingCompany && (
        <DeleteCompanyModal
          open={isDeleteCompanyDialogOpen}
          onClose={() => {
            setIsDeleteCompanyDialogOpen(false);
            setDeletingCompany(null);
          }}
          company={deletingCompany}
        />
      )}

      {/* Inactivate Branch Dialog */}
      {inactivatingBranch && (
        <InactivateBranchModal
          open={isInactivateBranchDialogOpen}
          onClose={() => {
            setIsInactivateBranchDialogOpen(false);
            setInactivatingBranch(null);
          }}
          branch={inactivatingBranch}
          onSuccess={() => {
            // Query invalidation is handled in the modal's onSuccess
            // Additional refetch can be triggered here if needed
          }}
        />
      )}
    </div>
  );
}

function CompanyCard({
  company,
  onEdit,
  onAddBranch,
  onEditBranch,
  onDeleteCompany,
  onInactivateBranch,
  formatWorkingHours,
  getCompanyInitials,
  roleLevel,
  canAccessAllBranches,
  userBranchId,
}: {
  company: DetailedCompany;
  onEdit: () => void;
  onAddBranch: () => void;
  onEditBranch: (branch: CompanyBranch) => void;
  onDeleteCompany: (company: DetailedCompany) => void;
  onInactivateBranch: (branch: CompanyBranch) => void;
  formatWorkingHours: (branch: CompanyBranch) => string;
  getCompanyInitials: (name: string) => string;
  roleLevel: number | null;
  canAccessAllBranches: boolean;
  userBranchId: number | null;
}) {
  const { data: branchesData, isLoading: isLoadingBranches } = useQuery({
    queryKey: queryKeys.company.branches(company.id),
    queryFn: async () => {
      const result = await getCompanyBranch({
        company_id: company.id,
      });
      return result;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  let branches = branchesData?.data || [];
  branches = branches.filter((branch) => branch.status === 1);

  if (!canAccessAllBranches && userBranchId !== null) {
    branches = branches.filter((branch) => branch.id === userBranchId);
  }

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        {/* Company Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
            <Avatar className="h-12 w-12 sm:h-14 sm:w-14 shrink-0">
              <AvatarImage src={company.logo || undefined} alt={company.name} />
              <AvatarFallback className="bg-primary/10 text-primary text-lg">
                {getCompanyInitials(company.name)}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-3 flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base sm:text-lg break-words">{company.name}</h3>
                {company.industry_type && (
                  <Badge variant="secondary">{company.industry_type}</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2 min-w-0 max-w-full">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="truncate">{company.company_email}</span>
                </div>
                {company.company_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 shrink-0" />
                    <span>{company.company_phone}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 shrink-0" />
                  <span>
                    {company.currency_symbol} {company.currency_name}
                  </span>
                </div>
                <div className="flex items-center gap-2 min-w-0 max-w-full">
                  <Clock className="h-4 w-4 shrink-0" />
                  <span className="truncate">{company.time_zone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 shrink-0" />
                  <span>{company.company_branch?.length || 0} branches</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 shrink-0" />
                  <span>
                    {(() => {
                      const companyTotal = company.total_employee_count || 0;
                      if (companyTotal > 0) return companyTotal;
                      if (branches.length > 0) {
                        return branches.reduce((sum, branch) => sum + (branch.employee_count || 0), 0);
                      }
                      if (company.company_branch && company.company_branch.length > 0) {
                        return company.company_branch.reduce((sum, branch) => sum + (branch.employee_count || 0), 0);
                      }
                      return 0;
                    })()}{' '}
                    total employees
                  </span>
                </div>
              </div>
            </div>
          </div>
          {roleLevel === 1 && (
            <div className="flex items-center gap-2 shrink-0 self-start">
              <Button variant="outline" size="sm" className="gap-2 flex-1 lg:flex-none" onClick={onEdit}>
                <Edit className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" size="sm" className="gap-2 flex-1 lg:flex-none" onClick={() => onDeleteCompany(company)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Branch Locations Section */}
        <div className="border-t pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <h4 className="text-base sm:text-lg font-semibold">Branch Locations</h4>
              <Badge variant="secondary">{company.company_branch?.length || branches.length || 0}</Badge>
            </div>
            {roleLevel === 1 && (
              <Button onClick={onAddBranch} size="sm" variant="outline" className="gap-2 w-full sm:w-auto">
                <Plus className="h-4 w-4" />
                Add Branch
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4">
          {isLoadingBranches ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : branches.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-8 w-8 mx-auto mb-2" />
              <p>No branches yet. Add your first branch to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {branches.map((branch) => {
                const isInactive = branch.status !== 1;
                return (
                  <Card key={branch.id} className={`relative ${isInactive ? 'opacity-75' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-5 w-5 text-primary" />
                          <h5 className="font-semibold text-base">{branch.name}</h5>
                        </div>
                        {roleLevel === 1 && (
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditBranch(branch)}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onInactivateBranch(branch)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={branch.branch_type === 'Head Office' ? 'default' : 'secondary'} className="text-xs">
                            {branch.branch_type}
                          </Badge>
                          {isInactive && (
                            <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-1.5 text-sm text-muted-foreground">
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                            <span className="line-clamp-2">{branch.address}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 shrink-0" />
                            <span>{branch.employee_count || 0} employees</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 shrink-0" />
                            <span>{formatWorkingHours(branch)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
